import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, FlaskConical, Factory, CalendarRange, Building2, Printer, Scissors,
  Cog, ShieldCheck, Boxes, Truck, Users, BarChart3, MonitorPlay, Bell, Settings,
  Menu, X, Sun, Moon, LogOut, Circle, Sparkles, Layers, TrendingUp, PackageCheck, Megaphone,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useRealtime } from "@/context/RealtimeContext";
import { useList } from "@/hooks/useApi";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/orders", label: "Orders", icon: Package },
  { to: "/sampling", label: "Sampling", icon: FlaskConical },
  { to: "/innovation-lab", label: "Innovation Lab", icon: Sparkles },
  { to: "/fabric-development", label: "Fabric Development", icon: Layers },
  { to: "/rnd-insights", label: "R&D Insights", icon: TrendingUp, rndOnly: true },
  { to: "/restock-requests", label: "Restock Requests", icon: PackageCheck, mgrOnly: true },
  { to: "/buyer-updates", label: "Buyer Notifications", icon: Megaphone, mgrOnly: true },
  { to: "/production", label: "Production", icon: Factory },
  { to: "/planning", label: "Planning", icon: CalendarRange },
  { to: "/departments", label: "Departments", icon: Building2 },
  { to: "/printing", label: "Printing", icon: Printer },
  { to: "/stitching", label: "Stitching", icon: Scissors },
  { to: "/machines", label: "Machines", icon: Cog },
  { to: "/quality", label: "Quality Control", icon: ShieldCheck },
  { to: "/packing", label: "Packing", icon: Boxes },
  { to: "/dispatch", label: "Dispatch", icon: Truck },
  { to: "/employees", label: "Employees", icon: Users },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/live-board", label: "Live Factory Board", icon: MonitorPlay },
  { to: "/notifications", label: "Notifications", icon: Bell },
  { to: "/admin", label: "Admin Settings", icon: Settings, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { connected } = useRealtime();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: notifs } = useList("notifications", "/notifications");
  const unread = (notifs || []).filter((n) => !n.read).length;

  const items = NAV.filter((n) => {
    if (n.adminOnly) return user?.role === "admin";
    if (n.rndOnly || n.mgrOnly) return user?.role === "admin" || user?.role === "production_manager";
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-full w-64 bg-card border-r border-border z-50 flex flex-col transition-transform duration-300",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border shrink-0">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
            <Factory className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none tracking-wide">TEXFLOW</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">MES / ERP</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative",
                    isActive
                      ? "bg-primary/12 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-primary" />}
                    <Icon className="w-[18px] h-[18px] shrink-0" />
                    <span className="flex-1">{n.label}</span>
                    {n.to === "/notifications" && unread > 0 && (
                      <span className="text-[10px] font-bold bg-destructive text-destructive-foreground rounded-full px-1.5 py-0.5">{unread}</span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="h-16 border-b border-border bg-card/70 backdrop-blur-md sticky top-0 z-30 px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 -ml-2 text-foreground" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2 text-xs font-semibold" data-testid="live-status">
              <span className={cn("live-dot w-2.5 h-2.5 rounded-full", connected ? "bg-success" : "bg-muted-foreground")} />
              <span className={connected ? "text-success" : "text-muted-foreground"}>
                {connected ? "LIVE" : "OFFLINE"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate("/notifications")} data-testid="header-bell" className="relative">
              <Bell className="w-[18px] h-[18px]" />
              {unread > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-destructive" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="w-[18px] h-[18px]" /> : <Moon className="w-[18px] h-[18px]" />}
            </Button>
            <div className="hidden sm:flex items-center gap-2.5 pl-2 border-l border-border ml-1">
              <div className="text-right leading-tight">
                <div className="text-sm font-semibold" data-testid="current-user-name">{user?.name || user?.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-primary font-semibold">{(user?.role || "").replace(/_/g, " ")}</div>
              </div>
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-sm">
                  {(user?.name || user?.email || "?")[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn"><LogOut className="w-[18px] h-[18px]" /></Button>
          </div>
        </header>

        <main className="p-4 sm:p-6 max-w-[1600px] mx-auto fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
